<template>
  <div>
    <empty-page image="nowifi" v-if="error.statusCode === -1">
      <h1>{{ $t('networkErrorTitle') }}</h1>
      <p>{{ $t('networkErrorText') }}</p>
      <ck-footer :title="$t('reloadPage')" @click.native="reloadPage" />
    </empty-page>
    <empty-page
      image="custom"
      v-else-if="
        error.statusCode === 502 &&
          error.message &&
          error.message.startsWith('energy')
      "
    >
      <ck-icon
        slot="image"
        name="ico-custom-budget-control-error-128px"
        size="128px"
      />
      <template v-if="error.message === 'energy-pv-optimisation'">
        <ck-icon
          slot="image"
          name="ico-custom-budget-control-error-128px"
          size="128px"
        />
        <h1>{{ $t('errorEnergyOptimisationTitle') }}</h1>
        <p v-html="$t('errorEnergyOptimisationText')"></p>
      </template>
      <template v-else-if="error.message === 'energy-pv-optimisation-dhw'">
        <ck-icon
          slot="image"
          name="ico-custom-budget-control-error-128px"
          size="128px"
        />
        <h1>{{ $t('errorEnergyOptimisationDhwTitle') }}</h1>
        <p v-html="$t('errorEnergyOptimisationDhwText')"></p>
      </template>
      <template v-else>
        <h1>{{ $t('errorBudgetControlTitle') }}</h1>
        <p v-html="$t('errorBudgetControlText')"></p>
      </template>
      <ck-footer :title="$t('quit')" @click.native="goToEnergy" />
    </empty-page>
    <empty-page
      image="nowifi"
      v-else-if="error.message === 'registration-failed'"
    >
      <h1>{{ $t('generalErrorTitle') }}</h1>
      <p>{{ $t('generalErrorText') }}</p>
      <ck-button
        v-if="showRegisterManually"
        :action="$t('registerManually')"
        full-width
        @click.native="registerManually"
      />
      <ck-button
        v-if="showGoToInstallationOverview"
        :action="$t('goBack')"
        class="secondary"
        full-width
        @click.native="goToInstallationOverview"
      />
      <p class="link" @click="cancelRegistration">{{ $t('cancel') }}</p>
    </empty-page>
    <empty-page image="nowifi" v-else>
      <h1>{{ $t('generalErrorTitle') }}</h1>
      <p>{{ $t('generalErrorText') }}</p>
      <ck-footer :title="$t('back')" @click.native="goToRoot" />
    </empty-page>
  </div>
</template>

<script>
import '@/plugins/global-css'
import CkFooter from '@/components/kit/CkFooter'
import CkButton from '@/components/kit/CkButton'
import EmptyPage from '@/components/shared-layout/EmptyPage'
export default {
  components: {
    CkFooter,
    EmptyPage,
    CkButton
  },
  props: {
    error: Object
  },
  computed: {
    validInstallations() {
      return this.$storage.getUniversal('registration.valid-installations')
    },
    registrationFlowType() {
      return this.$store.state.registration.flowType
    },
    showRegisterManually() {
      return (
        this.error?.message === 'registration-failed' &&
        this.validInstallations?.length &&
        (this.registrationFlowType === 'existingInstallation' ||
          this.registrationFlowType === 'existingInstallationFromBanner' ||
          this.registrationFlowType === 'commissioningCode')
      )
    },
    showGoToInstallationOverview() {
      return (
        this.error?.message === 'registration-failed' &&
        this.validInstallations?.length > 1 &&
        (this.registrationFlowType === 'existingInstallation' ||
          this.registrationFlowType === 'existingInstallationFromBanner' ||
          this.registrationFlowType === 'commissioningCode')
      )
    }
  },
  created() {
    this.$store.commit('setErrorPage', true)
  },
  beforeDestroy() {
    this.$store.commit('setErrorPage', false)
  },
  methods: {
    goToEnergy() {
      if (
        this.error?.statusCode === 502 &&
        this.error?.message?.startsWith('energy')
      ) {
        const language = this.$storage.getUniversal('language') || 'en'
        this.$router.push({
          name: 'launcher',
          query: { status: 'energy', preferred_language: language }
        })
      } else {
        this.$router.push({
          name: 'energy'
        })
      }
    },
    reloadPage() {
      this.$router.go()
    },
    goToRoot() {
      const language = this.$storage.getUniversal('language') || 'en'
      if (
        this.$store.state.pageHistory &&
        this.$store.state.pageHistory.length &&
        this.$store.state.pageHistory[
          this.$store.state.pageHistory.length - 1
        ].includes('energy')
      ) {
        this.$router.push({
          name: 'launcher',
          query: { status: 'energy', preferred_language: language }
        })
      } else {
        this.$router.push({
          name: 'launcher',
          query: { status: 'overview', preferred_language: language }
        })
      }
    },
    registerManually() {
      this.$store.dispatch('registration/clearRegistration')
      this.$store.commit('registration/setFlow', 'newInstallation')
      this.$store.commit(
        'registration/setCurrentScreen',
        'registration-scan-units-start'
      )
      this.$router.push({ name: 'registration-scan-units-start' })
    },
    async goToInstallationOverview() {
      const installation = await this.$storage.getUniversal(
        'scan-units.installation'
      )
      const updatedInstallation = {
        ...installation,
        registration_status: {
          success: false,
          errorMessage: this.error.message
        }
      }

      await this.$storage.setUniversal(
        'scan-units.installation',
        updatedInstallation
      )

      const updatedValidInstallations = this.validInstallations.map(
        (installation) => {
          if (
            installation.localInstallationId ===
            updatedInstallation.localInstallationId
          ) {
            installation.registrationFailed = true
          }
          return installation
        }
      )
      await this.$storage.setUniversal(
        'registration.valid-installations',
        updatedValidInstallations
      )
      await this.storeFailedAttempt(updatedInstallation)

      this.$store.commit(
        'registration/setCurrentScreen',
        'registration-installations-overview'
      )
      this.$store.commit('registration/pushToHistory', 'overview')
      this.$router.push({ name: 'registration-installations-overview' })
    },
    async storeFailedAttempt(installation) {
      const failedRegistration = {
        date: Date.now()
      }
      const installationIdentifier = await this.$store.dispatch(
        'installation/generateInstallationIdentifier',
        installation.units
      )
      failedRegistration.identifier = installationIdentifier
      const failedRegistrations = await this.$storage.getUniversal(
        'registration.failed-registrations'
      )
      const updatedFailedRegistrations = failedRegistrations
        ? [...failedRegistrations]
        : []
      updatedFailedRegistrations.push(failedRegistration)
      this.$storage.setUniversal(
        'registration.failed-registrations',
        updatedFailedRegistrations
      )
    },
    async cancelRegistration() {
      if (this.validInstallations?.length === 1) {
        const installation = await this.$storage.getUniversal(
          'scan-units.installation'
        )
        const updatedInstallation = {
          ...installation,
          registration_status: {
            success: false,
            errorMessage: this.error.message
          }
        }
        await this.$storage.setUniversal(
          'scan-units.installation',
          updatedInstallation
        )
        await this.storeFailedAttempt(updatedInstallation)
      }
      const language = this.$storage.getUniversal('language') || 'en'
      this.$store.dispatch('registration/clearRegistration')
      this.$router.push({
        name: 'launcher',
        query: { status: 'overview', preferred_language: language }
      })
    }
  }
}
</script>

<style lang="less" scoped>
@import (reference) '~@/assets/less/main.less';
.empty-page > .text > .btn.secondary {
  margin-top: 0;
}
</style>
